/**
 * Archive: the log of every composition, oldest work always retrievable. Each row shows when it was
 * created, when (and whether) it was rendered and published, its duration and clip count; clicking
 * the clip count pops the actual clips used, each a jump straight into the Analyser. The menu
 * reopens, duplicates, reuses with fresh clips (timing preserved) or deletes.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { TopBar } from '@/components/shell';
import { RowMenu } from '@/components/row-menu';
import { queryClient, trpc } from '@/lib/trpc';

export function ArchivePage() {
  const navigate = useNavigate();
  const [clipsFor, setClipsFor] = useState<{ id: string; name: string } | null>(null);
  const list = useQuery(trpc.compose.list.queryOptions({ templates: false }));
  const clips = useQuery(trpc.compose.clips.queryOptions({ draftId: clipsFor?.id ?? '' }, { enabled: clipsFor !== null }));
  const duplicate = useMutation(trpc.compose.duplicate.mutationOptions());
  const refreshSlot = useMutation(trpc.compose.refreshSlot.mutationOptions());
  const remove = useMutation(trpc.compose.remove.mutationOptions({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.compose.list.queryKey({ templates: false }) }),
  }));

  // A fast edit opens where it was made.
  const openInComposer = (draftId: string) => void navigate({ to: list.data?.find((r) => r.id === draftId)?.cutMode === 'fast' ? '/compose-fast' : '/compose', search: { draft: draftId } });
  const duplicateAndOpen = async (draftId: string) => {
    const copy = await duplicate.mutateAsync({ draftId, name: null, asTemplate: false });
    openInComposer(copy.draftId);
  };
  /** Duplicate, then swap every clip for a fresh match; the timing-preserving refresh keeps the beat. */
  const reuseWithFreshClips = async (row: { id: string; name: string | null; slots: number }) => {
    const copy = await duplicate.mutateAsync({ draftId: row.id, name: `Reuse of ${row.name ?? 'edit'}`.slice(0, 80), asTemplate: false });
    for (let i = 0; i < row.slots; i++) {
      try {
        await refreshSlot.mutateAsync({ draftId: copy.draftId, slotIndex: i, prompt: null });
      } catch {
        // Nothing else in the library fits this slot; the original clip stays.
      }
    }
    openInComposer(copy.draftId);
  };

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();
  const rowName = (r: { name: string | null; briefText: string }) => r.name ?? (r.briefText.slice(0, 40) || 'untitled edit');

  return (
    <>
      <TopBar title="Archive">
        <span className="sub crumb">Every composition, past and present</span>
      </TopBar>
      <section className="content">
        {list.isError && <div className="card pad error" role="alert">{list.error.message}</div>}
        <div className="card lib-table">
          <table className="tbl">
            <thead>
              <tr>
                <th>Composition</th>
                <th>Created</th>
                <th>Published</th>
                <th className="n">Duration</th>
                <th className="n">Clips</th>
                <th>Platforms</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.data?.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button type="button" className="link-btn" title="Open in the Composer" onClick={() => openInComposer(r.id)}>{rowName(r)}</button>
                  </td>
                  <td className="sub">{fmtDate(r.createdAt)}</td>
                  <td className="sub">
                    {r.lastRender && r.lastRender.kind === 'final' && r.lastRender.status === 'done'
                      ? `rendered ${fmtDate(r.lastRender.createdAt)}`
                      : r.lastRender
                        ? `${r.lastRender.kind} render ${r.lastRender.status}`
                        : 'not yet'}
                  </td>
                  <td className="n mono">{(r.targetMs / 1000).toFixed(1)} s</td>
                  <td className="n">
                    <button type="button" className="link-btn mono" title="See the clips used in this composition"
                      onClick={() => setClipsFor({ id: r.id, name: rowName(r) })}>{r.slots}</button>
                  </td>
                  <td className="sub">none yet</td>
                  <td className="menu-cell">
                    <RowMenu title="Composition actions" items={[
                      { label: 'Open in the Composer', onClick: () => openInComposer(r.id) },
                      { label: 'Duplicate and open', onClick: () => void duplicateAndOpen(r.id) },
                      { label: 'Reuse with fresh clips (timing kept)', onClick: () => void reuseWithFreshClips(r) },
                      null,
                      { label: 'Delete permanently', danger: true, onClick: () => { if (window.confirm(`Delete "${rowName(r)}" permanently?`)) remove.mutate({ draftId: r.id }); } },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(list.data?.length ?? 0) === 0 && !list.isLoading && <div className="empty">No compositions yet; the Composer saves every edit here automatically.</div>}
        </div>
        {(duplicate.isError || remove.isError || refreshSlot.isError) && (
          <div className="card pad error" role="alert">{duplicate.error?.message ?? remove.error?.message ?? refreshSlot.error?.message}</div>
        )}
        {clipsFor && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setClipsFor(null)} />
            <div className="modal picker-modal" role="dialog" aria-label={`Clips in ${clipsFor.name}`}>
              <div className="row between"><h2>Clips in {clipsFor.name}</h2></div>
              <div className="picker-list">
                {clips.data?.map((c) => (
                  <button key={`${c.snippetId}-${c.slot}`} type="button" className="picker-row" disabled={!c.assetId}
                    title="Open this clip in the Analyser"
                    onClick={() => { if (c.assetId) void navigate({ to: '/analyse', search: { asset: c.assetId, snippet: c.snippetId } }); }}>
                    {c.posterUrl ? <img className="picker-thumb" src={c.posterUrl} alt="" /> : <span className="picker-thumb empty-poster" />}
                    <span className="dd-label">{c.slot}. {c.title ?? c.role ?? 'clip'}{c.assetName ? ` · ${c.assetName}` : ''}</span>
                    <span className="sub dd-n mono">{((c.outMs - c.inMs) / 1000).toFixed(1)} s</span>
                  </button>
                ))}
                {clips.isLoading && <div className="empty"><span className="spinner" aria-label="Loading" /></div>}
              </div>
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setClipsFor(null)}>Close</button>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}
